<template>
    <div class="flex flex-col dark:text-white">
        <h3
            class="font-large item-center my-3 flex text-xl leading-6 dark:text-white"
        >
            Campanhas
            <button
                type="button"
                class="ml-3 mt-3 inline-flex w-full items-center justify-center rounded-md border border-indigo-500 bg-indigo-500 px-4 py-1 text-base font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                style="margin-right: auto"
                @click="createCampaign()"
            >
                Nova campanha
            </button>
        </h3>
        <filters
            class="mt-3"
            :loading="loading"
            v-model="filters"
            date-filter
            status-filter
            :statuses="[
                { value: true, label: 'Ativo' },
                { value: false, label: 'Inativo' },
            ]"
            search-filter
            search-label="Filtre pelo id, produto ou produtor"
            @fetch="
                campaigns = [];
                next_page_url = false;
                this.pagination.current_page = 1;
                fetch();
            "
        >
        </filters>
        <table-component
            :loading="loading"
            :pagination="pagination"
            :headers="headers"
            select-all
            @fetch="fetch"
            @selectAll="selectAll"
            :selected="selected"
            :all-selected="all_selected"
        >
            <tr v-for="campaign in campaigns" :key="campaign.id">
                <td class="table-cell-sm">
                    <div class="flex">
                        <input
                            :id="'check-campaign-' + campaign.id"
                            name="campaigns"
                            type="checkbox"
                            class="mr-3 h-4 w-4 rounded border-red-300 text-red-300 checked:shadow-xl focus:ring-indigo-500"
                            v-model="selected"
                            :value="campaign.id"
                        />
                        <a
                            @click.prevent="manage(campaign)"
                            class="flex items-center text-indigo-400 hover:cursor-pointer hover:text-indigo-700 hover:underline"
                        >
                            #{{ campaign.id }}
                        </a>
                    </div>
                </td>
                <td class="table-cell-sm">
                    {{ campaign.created_at }}
                </td>
                <td class="table-cell-sm">
                    <span
                        v-if="campaign.product_id"
                        :title="campaign.product_id"
                        class="block max-w-[80px] truncate"
                        v-tooltip="campaign.product_id"
                    >
                        {{ campaign.product_id }}
                    </span>
                </td>
                <td class="table-cell-sm">
                    <span
                        v-if="campaign.producer_id"
                        :title="campaign.producer_id"
                        class="block max-w-[80px] truncate"
                        v-tooltip="campaign.producer_id"
                    >
                        {{ campaign.producer_id }}
                    </span>
                </td>
                <td class="table-cell-sm">
                    {{ campaign.start_date }}
                </td>
                <td class="table-cell-sm">
                    {{ campaign.end_date }}
                </td>
                <td class="table-cell-sm">
                    <div class="item-center flex">
                        <component
                            :is="
                                campaign.is_active
                                    ? 'CheckCircleIcon'
                                    : 'MinusCircleIcon'
                            "
                            class="mr-1.5 h-5 w-5 shrink-0"
                            :class="{
                                'text-red-400': !campaign.is_active,
                                'text-green-400': campaign.is_active,
                            }"
                        />
                        {{ campaign.is_active ? "Ativo" : "Inativo" }}
                    </div>
                </td>
                <td class="table-cell-sm">
                    {{ campaign.created_by }}
                </td>
                <td class="table-cell-sm">
                    {{ campaign.updated_at }}
                </td>
                <td class="table-cell-sm">
                    <div>
                        <button
                            v-if="hasPermission('update.campaigns')"
                            @click.prevent="manage(campaign)"
                            :disabled="loading"
                            type="button"
                            :class="{ 'cursor-not-allowed': loading }"
                            class="ml-2 inline-flex items-center justify-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                        >
                            <PencilSquareIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-white"
                            />
                        </button>
                        <button
                            v-if="hasPermission('delete.campaigns')"
                            @click.prevent="handleDeleteCampaign(campaign.id)"
                            :disabled="loading"
                            type="button"
                            :class="{ 'cursor-not-allowed': loading }"
                            class="ml-2 inline-flex items-center justify-center rounded-md bg-red-500 px-2 py-1 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-red-400"
                        >
                            <TrashIcon
                                class="h-5 w-5 shrink-0 text-gray-400 text-white"
                            />
                        </button>
                    </div>
                </td>
            </tr>
        </table-component>
    </div>
</template>

<script>
import {
    MinusCircleIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import { mapWritableState } from "pinia";
import Filters from "../../components/layouts/Filters.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import { Store } from "../../stores/store.js";
import permissionMixin from "../../mixins/PermissionMixin.vue";
import { useToast } from "vue-toastification";

export default {
    components: {
        Filters,
        TableComponent,
        CheckCircleIcon,
        MinusCircleIcon,
        PencilSquareIcon,
        TrashIcon,
    },
    mixins: [permissionMixin],
    data() {
        return {
            loading: false,
            next_page_url: false,
            selected: [],
            all_selected: false,
            campaigns: [],
            editCampaign: null,
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 11,
                from: 1,
                to: 11,
            },
            filters: {
                text: "",
                status: [],
            },
            headers: [
                "",
                "Data",
                "Produto",
                "Produtor",
                "Data Início",
                "Data Fim",
                "Status",
                "Criado Por",
                "Última Modificação",
                "Ações",
            ],
        };
    },

    setup() {
        const toast = useToast();
        return { toast };
    },

    computed: {
        ...mapWritableState(Store, ["storeCampaign"]),

        filteredStatuses() {
            return this.filters.status.map((status) => {
                return {
                    value: status,
                    label: this.statuses.find((s) => s.value === status).label,
                };
            });
        },
    },

    mounted() {
        this.storeCampaign = false;
        this.fetch();
    },

    methods: {
        manage(campaign) {
            this.storeCampaign = campaign;
            this.$router.push({
                name: "campaign",
                params: { campaign_id: campaign.id },
            });
        },
        createCampaign() {
            this.storeCampaign = false;
            this.$router.push({
                name: "campaign.create",
            });
        },
        openModalToEdit(campaign) {
            this.editCampaign = campaign;
        },
        handleDeleteCampaign(id) {
            if (confirm("Tem certeza que deseja excluir esta campanha?")) {
                axios
                    .delete(`/api/campaigns/${id}`)
                    .then(({ data }) => {
                        if (data ?? null) {
                            this.toast.success("Campanha excluída com sucesso");
                            this.fetch();
                        }
                    })
                    .catch(
                        ({
                            response: {
                                data: { message },
                            },
                        }) => {
                            this.toast.error(message);
                        },
                    );
            }
        },
        selectAll() {
            this.all_selected = !this.all_selected;
            if (this.all_selected) {
                this.selected = this.campaigns.map((campaign) => campaign.id);
            } else {
                this.selected = [];
            }
        },
        fetch() {
            this.loading = true;

            const params = this.filters;
            params["page"] = this.pagination.current_page;

            axios
                .get("/api/campaigns", {
                    params: params,
                })
                .then(({ data }) => {
                    this.campaigns = data.data;

                    this.pagination = {
                        ...this.pagination,
                        ...data.meta,
                    };

                    this.next_page_url = data.next_page_url;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    console.log(error);
                    this.toast.error(error.response.data.message);
                });
        },
    },
};
</script>
